import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useWishlist } from '../../../context/WishlistContext';

const ProductCard = ({ product, onAddToCart, onToggleWishlist }) => {
  const wishlist = useWishlist();
  const navigate = useNavigate();
  const isWishlisted = wishlist.has(product?.id);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddToCart = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (onAddToCart) onAddToCart(product);
  };

  const handleToggleWishlist = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    wishlist.toggle(product);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
      );
    }
    
    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="Star" size={12} className="text-warning fill-current opacity-50" />
      );
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-muted-foreground" />
      );
    }
    
    return stars;
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/product-details?id=${product?.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/product-details?id=${product?.id}`); }}
      className="group block bg-card rounded-xl border border-border hover:shadow-elevation-2 transition-all duration-300 overflow-hidden cursor-pointer"
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-muted">
        <Image
          src={product?.image}
          alt={product?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product?.isNew && (
            <span className="px-2 py-1 bg-success text-success-foreground text-xs font-medium rounded-full">
              New
            </span>
          )}
          {product?.discount > 0 && (
            <span className="px-2 py-1 bg-error text-error-foreground text-xs font-medium rounded-full">
              -{product?.discount}%
            </span>
          )}
          {product?.isBestseller && (
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
              Bestseller
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleToggleWishlist}
          className="absolute top-3 right-3 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-colors"
        >
          <Icon 
            name="Heart" 
            size={16} 
            className={isWishlisted ? "text-error fill-current" : "text-muted-foreground"} 
          />
        </button>

        {/* Quick Actions (visible on hover) */}
        <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            loading={isLoading}
            onClick={handleAddToCart}
            iconName="ShoppingCart"
            iconPosition="left"
          >
            Add to Cart
          </Button>
        </div>
      </div>
      {/* Product Info */}
      <div className="p-4">
        {/* Brand */}
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">
          {product?.brand}
        </p>

        {/* Product Name */}
        <h3 className="text-sm font-medium text-foreground line-clamp-2 mb-2 group-hover:text-primary transition-colors">
          {product?.name}
        </h3>

        {/* Rating & Reviews */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            {renderStars(product?.rating)}
          </div>
          <span className="text-xs text-muted-foreground">
            ({product?.reviewCount})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg font-semibold text-foreground">
            ${product?.price?.toFixed(2)}
          </span>
          {product?.originalPrice > 0 && product?.originalPrice > product?.price && (
            <span className="text-sm text-muted-foreground line-through">
              ${product?.originalPrice?.toFixed(2)}
            </span>
          )}
        </div>

        {/* Variants Preview */}
        {product?.variants && product?.variants?.length > 0 && (
          <div className="flex items-center gap-1 mb-3">
            <span className="text-xs text-muted-foreground">Colors:</span>
            <div className="flex gap-1">
              {product?.variants?.slice(0, 4)?.map((variant, index) => (
                <div
                  key={index}
                  className="w-4 h-4 rounded-full border border-border"
                  style={{ backgroundColor: variant?.color }}
                  title={variant?.name}
                />
              ))}
              {product?.variants?.length > 4 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{product?.variants?.length - 4}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stock Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${
              product?.stock > 10 ? 'bg-success' : 
              product?.stock > 0 ? 'bg-warning' : 'bg-error'
            }`} />
            <span className="text-xs text-muted-foreground">
              {product?.stock > 10 ? 'In Stock' : 
               product?.stock > 0 ? `${product?.stock} left` : 'Out of Stock'}
            </span>
          </div>
          
          {product?.freeShipping && (
            <span className="text-xs text-success font-medium">Free Shipping</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;